import Vue from 'vue';
import { listSessionsLightweight, createSession } from '@/api/client';
import ProjectCard from '@/components/ProjectCard.vue';
import CategoryCard from '@/components/CategoryCard.vue';
import CategoryGrid from '@/components/CategoryGrid.vue';
import SessionListCards from '@/components/SessionListCards.vue';
import SessionCountSettings from '@/components/SessionCountSettings.vue';
import AppHeader from '@/components/AppHeader.vue';
import { organizationStore } from '@/stores/organizationStore';
import GlobalSearch from '@/components/GlobalSearch.vue';
import OrganizationSwitcher from '@/components/OrganizationSwitcher.vue';
import { isEmbedMode } from '@/lib/embedMode';
import { toastMixin } from '@/mixins/toastMixin';
import { sessionCountStore } from '@/stores/sessionCountStore';
import { getUserPreferences, saveUserPreferences } from '@/api/preferences';
import { dlog } from '@/lib/debugLog';
export default Vue.extend({
    name: 'SessionList',
    mixins: [toastMixin],
    components: { ProjectCard, CategoryCard, CategoryGrid, SessionListCards, SessionCountSettings, AppHeader, GlobalSearch, OrganizationSwitcher },
    data() {
        return {
            sessions: [],
            newSessionName: '',
            error: null,
            sessionsLoaded: false,
            isLoadingMore: false,
            sessionCountStore,
            expandedCategories: {},
            expandedProjects: new Set(),
            expandedCategoriesSet: new Set(),
            highlightedSessionId: null, // For MutationObserver-based navigation
        };
    },
    computed: {
        // Task A6: hide org switcher chrome inside the Sparkco iframe
        isEmbedChrome() {
            return isEmbedMode();
        },
        allGroupedProjects() {
            if (!this.sessionsLoaded) {
                // Wait until sessions are loaded
                return [];
            }
            this.addDebugLog("[DEBUG-100] [allGroupedProjects()] - START - Computing all grouped projects from sessions");
            // Extend Session type to include project and category strings
            const sessions = this.sessions;
            this.addDebugLog(`[DEBUG-110] [allGroupedProjects()] - Sessions loaded: ${sessions.length}`);
            const projMap = new Map();
            console.log("[DEBUG-120] [allGroupedProjects()] - Grouping sessions by project and category");
            sessions.forEach((s) => {
                const projectKey = s.project;
                const categoryKey = s.category;
                if (!projMap.has(projectKey)) {
                    projMap.set(projectKey, new Map());
                }
                const catMap = projMap.get(projectKey);
                if (!catMap.has(categoryKey)) {
                    catMap.set(categoryKey, []);
                }
                catMap.get(categoryKey).push(s);
            });
            const projects = [];
            console.log("[DEBUG-130] [allGroupedProjects()] - Grouping sessions into projects and categories");
            // Convert Map to array and sort projects alphabetically
            const sortedProjectEntries = Array.from(projMap.entries()).sort((a, b) => a[0].localeCompare(b[0], undefined, { sensitivity: 'base' }));
            sortedProjectEntries.forEach(([proj, catMap]) => {
                const categories = [];
                // Convert Map to array and sort categories alphabetically
                const sortedCategoryEntries = Array.from(catMap.entries()).sort((a, b) => a[0].localeCompare(b[0], undefined, { sensitivity: 'base' }));
                sortedCategoryEntries.forEach(([cat, sessArr]) => {
                    // Sort sessions by date (latest first) within each category
                    const sortedSessions = sessArr.sort((a, b) => {
                        const dateA = new Date(a.createdAt).getTime();
                        const dateB = new Date(b.createdAt).getTime();
                        return dateB - dateA; // Descending order (newest first)
                    });
                    categories.push({ name: cat, sessions: sortedSessions });
                });
                projects.push({ name: proj, categories });
            });
            console.log(`[DEBUG-190] [allGroupedProjects()] - Computed ${projects.length} projects with categories (sorted alphabetically)`);
            return projects;
        },
        paginatedProjects() {
            const limit = this.sessionCountStore.sessionsPerPage;
            if (limit === 'all') {
                console.log('[DEBUG-200] [paginatedProjects()] - Showing all sessions');
                // Add projectId and categoryKey to all projects/categories when showing all sessions
                return this.allGroupedProjects.map(project => ({
                    ...project,
                    categories: project.categories.map(category => ({
                        ...category,
                        categoryKey: `${project.name}|${category.name}` // CRITICAL: Add categoryKey for navigation
                    })),
                    projectId: this.getProjectIdFromSessions(project)
                }));
            }
            console.log(`[DEBUG-200] [paginatedProjects()] - Limiting to ${limit} sessions per category with expansion support`);
            return this.limitSessionsInProjectsWithExpansion(this.allGroupedProjects, limit);
        },
        totalSessionsCount() {
            return this.allGroupedProjects.reduce((total, project) => total + project.categories.reduce((catTotal, category) => catTotal + category.sessions.length, 0), 0);
        },
        visibleSessionsCount() {
            return this.paginatedProjects.reduce((total, project) => total + project.categories.reduce((catTotal, category) => catTotal + category.sessions.length, 0), 0);
        },
        hasMoreSessions() {
            const limit = this.sessionCountStore.sessionsPerPage;
            if (limit === 'all')
                return false;
            return this.visibleSessionsCount < this.totalSessionsCount;
        },
        remainingSessionsCount() {
            return this.totalSessionsCount - this.visibleSessionsCount;
        }
    },
    methods: {
        async loadUserPreferences() {
            try {
                const prefs = await getUserPreferences();
                if (prefs) {
                    // Load expanded projects
                    if (prefs.expandedProjects) {
                        this.expandedProjects = new Set(prefs.expandedProjects);
                    }
                    // Load expanded categories
                    if (prefs.expandedCategories) {
                        this.expandedCategoriesSet = new Set(prefs.expandedCategories);
                    }
                    console.log('[SessionList] Loaded user preferences:', {
                        expandedProjects: prefs.expandedProjects,
                        expandedCategories: prefs.expandedCategories
                    });
                }
            }
            catch (error) {
                console.error('[SessionList] Failed to load user preferences:', error);
            }
        },
        async saveExpandedState() {
            try {
                const saved = await saveUserPreferences({
                    expandedProjects: Array.from(this.expandedProjects),
                    expandedCategories: Array.from(this.expandedCategoriesSet)
                });
                if (saved === true) {
                    dlog('[SessionList] Saved expand/collapse preferences');
                }
                else if (saved === false) {
                    console.warn('[SessionList] Expand/collapse preferences not saved to server (request failed)');
                }
            }
            catch (error) {
                console.error('[SessionList] Failed to save preferences:', error);
            }
        },
        isProjectExpanded(projectName) {
            return this.expandedProjects.has(projectName);
        },
        isCategoryExpanded(projectName, categoryName) {
            const categoryKey = `${projectName}|${categoryName}`;
            return this.expandedCategoriesSet.has(categoryKey);
        },
        onProjectExpandToggled(event) {
            if (event.expanded) {
                this.expandedProjects.add(event.project);
            }
            else {
                this.expandedProjects.delete(event.project);
            }
            this.saveExpandedState();
        },
        onCategoryExpandToggled(projectName, event) {
            const categoryKey = `${projectName}|${event.category}`;
            if (event.expanded) {
                this.expandedCategoriesSet.add(categoryKey);
            }
            else {
                this.expandedCategoriesSet.delete(categoryKey);
            }
            this.saveExpandedState();
        },
        onExpandAllCategories(event) {
            console.log('[SessionList] Expanding all categories in project:', event.project);
            // Find the project and expand all its categories
            const project = this.allGroupedProjects.find(p => p.name === event.project);
            if (project) {
                project.categories.forEach(category => {
                    const categoryKey = `${event.project}|${category.name}`;
                    this.expandedCategoriesSet.add(categoryKey);
                });
                this.saveExpandedState();
                // Force component re-render by creating new Set
                this.expandedCategoriesSet = new Set(this.expandedCategoriesSet);
            }
        },
        onCollapseAllCategories(event) {
            console.log('[SessionList] Collapsing all categories in project:', event.project);
            // Find the project and collapse all its categories
            const project = this.allGroupedProjects.find(p => p.name === event.project);
            if (project) {
                project.categories.forEach(category => {
                    const categoryKey = `${event.project}|${category.name}`;
                    this.expandedCategoriesSet.delete(categoryKey);
                });
                this.saveExpandedState();
                // Force component re-render by creating new Set
                this.expandedCategoriesSet = new Set(this.expandedCategoriesSet);
            }
        },
        /**
         * Wait for element to appear in DOM using MutationObserver (with timeout)
         */
        async waitForElement(selector, timeout = 5000) {
            return new Promise((resolve) => {
                // Check if element already exists
                const existing = document.querySelector(selector);
                if (existing) {
                    console.log(`[Navigation] ✅ Element found immediately: ${selector}`);
                    resolve(existing);
                    return;
                }
                console.log(`[Navigation] ⏳ Waiting for element: ${selector}`);
                // Setup MutationObserver to watch for element
                const observer = new MutationObserver(() => {
                    const element = document.querySelector(selector);
                    if (element) {
                        console.log(`[Navigation] ✅ Element appeared: ${selector}`);
                        observer.disconnect();
                        resolve(element);
                    }
                });
                observer.observe(document.body, {
                    childList: true,
                    subtree: true,
                });
                // Timeout fallback
                setTimeout(() => {
                    console.warn(`[Navigation] ⚠️ Timeout (${timeout}ms) waiting for: ${selector}`);
                    observer.disconnect();
                    resolve(null);
                }, timeout);
            });
        },
        /**
         * Expand project and category (async with verification)
         */
        async expandProjectCategoryAsync(project, category) {
            console.log(`[Navigation] 📂 Expanding: ${project} / ${category}`);
            const categoryKey = `${project}|${category}`;
            // CRITICAL FIX: Force expansion by removing then adding
            // This ensures watchers will fire even if state thinks it's expanded
            console.log(`[Navigation] 🔄 Force-refreshing expansion state`);
            // Remove from sets first (force state change)
            this.expandedProjects.delete(project);
            this.expandedCategoriesSet.delete(categoryKey);
            // Create new sets to trigger reactivity
            this.expandedProjects = new Set(this.expandedProjects);
            this.expandedCategoriesSet = new Set(this.expandedCategoriesSet);
            // Force update and wait
            this.$forceUpdate();
            await this.$nextTick();
            // Small delay to ensure collapse happens
            await new Promise(resolve => setTimeout(resolve, 50));
            // Now add them back (force expansion)
            console.log(`[Navigation] 🔓 Expanding project: ${project}`);
            this.expandedProjects.add(project);
            console.log(`[Navigation] 🔓 Expanding category: ${categoryKey}`);
            this.expandedCategoriesSet.add(categoryKey);
            // Create new sets again to trigger reactivity
            this.expandedProjects = new Set(this.expandedProjects);
            this.expandedCategoriesSet = new Set(this.expandedCategoriesSet);
            // Force component update
            this.$forceUpdate();
            // Save state
            this.saveExpandedState();
            // Wait for Vue to update
            await this.$nextTick();
            // Extra delay for animations
            await new Promise(resolve => setTimeout(resolve, 300));
            console.log(`[Navigation] ✅ Expansion complete`);
        },
        /**
         * Handle search navigation with MutationObserver (expand → scroll → highlight)
         */
        async handleSearchNavigation() {
            const { sessionId, project, category } = this.$route.query;
            // No navigation params
            if (!project) {
                return;
            }
            console.log('[Navigation] 🎯 Starting navigation:', {
                sessionId,
                project,
                category,
            });
            // NOTE: We now force-refresh expansion state, so stale state is less of an issue
            try {
                // Check what type of navigation this is
                const isProjectOnly = !category || category === '';
                const isCategoryNav = category && category !== '';
                const hasSession = sessionId && sessionId !== '';
                if (isProjectOnly) {
                    // Project-only navigation
                    console.log('[Navigation] 📁 Project-only navigation');
                    // CRITICAL FIX: Force expansion by removing then adding
                    console.log(`[Navigation] 🔄 Force-refreshing project expansion state`);
                    // Remove from set first (force state change)
                    this.expandedProjects.delete(project);
                    // Create new set to trigger reactivity
                    this.expandedProjects = new Set(this.expandedProjects);
                    // Force update and wait
                    this.$forceUpdate();
                    await this.$nextTick();
                    // Small delay to ensure collapse happens
                    await new Promise(resolve => setTimeout(resolve, 50));
                    // Now add it back (force expansion)
                    console.log(`[Navigation] 🔓 Expanding project: ${project}`);
                    this.expandedProjects.add(project);
                    // Create new set again to trigger reactivity
                    this.expandedProjects = new Set(this.expandedProjects);
                    // Force component update
                    this.$forceUpdate();
                    // Save state
                    this.saveExpandedState();
                    // Wait for Vue to update
                    await this.$nextTick();
                    // Extra delay for animations
                    await new Promise(resolve => setTimeout(resolve, 300));
                    // Wait for project element and scroll to it
                    const projectSelector = `[data-project-name="${project}"]`;
                    const projectElement = await this.waitForElement(projectSelector, 5000);
                    if (projectElement) {
                        console.log('[Navigation] 📜 Scrolling to project:', project);
                        projectElement.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                        });
                        // Highlight project
                        setTimeout(() => {
                            projectElement.classList.add('highlight-flash');
                            setTimeout(() => {
                                projectElement.classList.remove('highlight-flash');
                            }, 4000);
                        }, 600);
                    }
                }
                else if (isCategoryNav) {
                    // Category navigation (with or without session)
                    console.log('[Navigation] 📂 Category navigation');
                    // Step 1: Expand project and category
                    await this.expandProjectCategoryAsync(project, category);
                    if (hasSession) {
                        // Step 2: Wait for session card to exist in DOM
                        const selector = `[data-session-id="${sessionId}"]`;
                        const element = await this.waitForElement(selector, 5000);
                        if (!element) {
                            console.error('[Navigation] ❌ Session card not found:', sessionId);
                            // Fall back to category scroll
                            const categorySelector = `[data-category-key="${project}|${category}"]`;
                            const categoryElement = await this.waitForElement(categorySelector, 2000);
                            if (categoryElement) {
                                categoryElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                            return;
                        }
                        // Step 3: Scroll into view
                        console.log('[Navigation] 📜 Scrolling to session:', sessionId);
                        element.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                        });
                        // Step 4: Highlight session
                        setTimeout(() => {
                            console.log('[Navigation] ✨ Highlighting session:', sessionId);
                            this.highlightedSessionId = sessionId;
                            // Clear highlight after 4s
                            setTimeout(() => {
                                console.log('[Navigation] 🔚 Clearing highlight');
                                this.highlightedSessionId = null;
                            }, 4000);
                        }, 600);
                    }
                    else {
                        // Category navigation without session - scroll to category
                        const categorySelector = `[data-category-key="${project}|${category}"]`;
                        const categoryElement = await this.waitForElement(categorySelector, 5000);
                        if (categoryElement) {
                            console.log('[Navigation] 📜 Scrolling to category:', category);
                            categoryElement.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center',
                            });
                            // Highlight category
                            setTimeout(() => {
                                categoryElement.classList.add('highlight-flash');
                                setTimeout(() => {
                                    categoryElement.classList.remove('highlight-flash');
                                }, 4000);
                            }, 600);
                        }
                    }
                }
                // Step 5: Clean up query params
                this.$router.replace({ query: {} });
                console.log('[Navigation] ✅ Navigation complete');
            }
            catch (error) {
                console.error('[Navigation] ❌ Navigation failed:', error);
            }
        },
        // Navigation handlers for GlobalSearch
        navigateToSession(sessionId) {
            this.$router.push({ name: 'session', params: { id: sessionId } });
        },
        navigateToProjectWithSession(payload) {
            console.log('[SessionList] Navigate to project with session highlight:', payload);
            // Use query params to trigger navigation (will be handled by handleSearchNavigation)
            this.$router.push({
                query: {
                    project: payload.project,
                    category: '',
                    sessionId: payload.sessionId || ''
                }
            });
        },
        navigateToCategoryWithSession(payload) {
            console.log('[SessionList] Navigate to category with session highlight:', payload);
            // Use query params to trigger navigation (will be handled by handleSearchNavigation)
            this.$router.push({
                query: {
                    project: payload.project,
                    category: payload.category,
                    sessionId: payload.sessionId || ''
                }
            });
        },
        highlightSession(sessionId) {
            console.log('[SessionList] Highlighting session:', sessionId);
            // Find session card by data-session-id attribute
            const sessionCard = document.querySelector(`[data-session-id="${sessionId}"]`);
            console.log('[SessionList] Session card found:', !!sessionCard, sessionId);
            if (sessionCard) {
                // Scroll session into view
                sessionCard.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest'
                });
                // Add highlight flash
                sessionCard.classList.add('highlight-flash');
                console.log('[SessionList] Added highlight-flash to session card');
                // Remove highlight after 4 seconds
                setTimeout(() => {
                    sessionCard.classList.remove('highlight-flash');
                    console.log('[SessionList] Removed highlight-flash from session card');
                }, 4000);
            }
            else {
                console.error('[SessionList] ❌ Session card NOT FOUND:', sessionId);
            }
        },
        navigateToCategory(project, category) {
            console.log('[SessionList] Navigate to category:', { project, category });
            // Use query params to trigger navigation
            this.$router.push({
                query: {
                    project: project,
                    category: category,
                    sessionId: ''
                }
            });
        },
        navigateToProject(project) {
            console.log('[SessionList] Navigate to project:', project);
            // Use query params to trigger navigation
            this.$router.push({
                query: {
                    project: project,
                    category: '',
                    sessionId: ''
                }
            });
        },
        async loadSessions() {
            const startTime = performance.now();
            const orgId = organizationStore.currentOrgId;
            console.log(`[DEBUG-200] [loadSessions()] - START - Loading lightweight sessions (PERFORMANCE OPTIMIZED)${orgId ? ` for org ${orgId}` : ''}`);
            try {
                // Load LIGHTWEIGHT sessions (no transcripts) for fast page load
                // Transcripts will be loaded on-demand when user opens a session
                // Filter by organization if user is logged in
                const result = await listSessionsLightweight(orgId || undefined);
                const endTime = performance.now();
                const loadTime = Math.round(endTime - startTime);
                console.log('[DEBUG-201] Raw result from listSessionsLightweight():', result);
                if (!Array.isArray(result)) {
                    console.error('[ERROR] Expected array but got:', typeof result, result);
                    this.error = 'Invalid response format. Expected a list of sessions.';
                    this.sessions = [];
                }
                else {
                    this.sessions = result;
                    this.error = null;
                    console.log(`[DEBUG-202] [loadSessions()] - Lightweight sessions loaded: ${result.length} in ${loadTime}ms`);
                    // Show performance popup
                    this.showPerformancePopup(loadTime, result.length);
                }
            }
            catch (err) {
                const endTime = performance.now();
                const loadTime = Math.round(endTime - startTime);
                console.error('[ERROR] Failed to load sessions:', err);
                this.error = 'Failed to load sessions. Check backend.';
                this.sessions = [];
                this.showPerformancePopup(loadTime, 0, true);
            }
            this.sessionsLoaded = true;
            console.log("[DEBUG-203] [loadSessions()] - END - Session loading process completed");
        },
        showPerformancePopup(loadTime, sessionCount, failed = false) {
            // Calculate visible sessions based on user preference
            const visibleSessions = this.sessionCountStore.sessionsPerPage === 'all'
                ? sessionCount
                : this.visibleSessionsCount;
            const message = failed
                ? `❌ Failed to load sessions (${loadTime}ms)`
                : `⚡ Loaded ${sessionCount} sessions, showing ${visibleSessions} (${loadTime}ms)`;
            console.log(`[PERFORMANCE] ${message}`);
            this.showToastMessage(message);
            // Send performance metrics to backend API for CSV logging
            this.sendPerformanceMetrics(loadTime, sessionCount, visibleSessions, failed);
        },
        async sendPerformanceMetrics(loadTime, totalSessions, visibleSessions, failed) {
            try {
                const sessionLimit = this.sessionCountStore.sessionsPerPage;
                await fetch(`${process.env.VUE_APP_API_URL_BE}/metrics/page-load`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'ngrok-skip-browser-warning': 'true'
                    },
                    body: JSON.stringify({
                        timestamp: new Date().toISOString(),
                        loadTime,
                        totalSessions,
                        visibleSessions,
                        sessionLimit,
                        failed
                    })
                });
                console.log('[PERFORMANCE] Metrics sent to backend');
            }
            catch (error) {
                console.error('[PERFORMANCE] Failed to send metrics:', error);
                // Don't show error to user - metrics are optional
            }
        },
        addDebugLog(message) {
            console.log(message);
            //this.debugLogs.push(message);
        },
        // Pipeline creation: split input "Project|Category|Session" or "Project|Session"
        async pipelineCreate() {
            console.log("[DEBUG-300] [pipelineCreate()] - START - Creating session from input");
            if (!this.newSessionName)
                return;
            const parts = this.newSessionName.split('|').map(p => p.trim());
            let project = "Project", category = "Generic", name = "";
            if (parts.length === 2) {
                [project, name] = parts;
            }
            else if (parts.length >= 3) {
                [project, category, name] = parts;
            }
            else {
                name = parts[0];
            }
            try {
                console.log(`[DEBUG-301] [pipelineCreate()] - Creating session with project: ${project}, category: ${category}, name: ${name}`);
                // Cast the argument as any to match createSession's expected type (fix TS2345)
                await createSession(project, category, name, "Created from the session list");
                await this.loadSessions();
                this.newSessionName = '';
                console.log("[DEBUG-302] [pipelineCreate()] - Session created successfully");
            }
            catch {
                this.error = 'Failed to create session.';
            }
            console.log("[DEBUG-303] [pipelineCreate()] - END - Session creation process completed");
        },
        getProjectSessionCount(project) {
            return project.categories.reduce((total, category) => total + category.sessions.length, 0);
        },
        getTotalProjectSessionCount(projectName) {
            const originalProject = this.allGroupedProjects.find(p => p.name === projectName);
            if (!originalProject)
                return 0;
            return originalProject.categories.reduce((total, category) => total + category.sessions.length, 0);
        },
        getTotalCategorySessionCount(projectName, categoryName) {
            const originalProject = this.allGroupedProjects.find(p => p.name === projectName);
            if (!originalProject)
                return 0;
            const originalCategory = originalProject.categories.find(c => c.name === categoryName);
            if (!originalCategory)
                return 0;
            return originalCategory.sessions.length;
        },
        // ✅ NEW: Check if project has any sessions with gems (client-side, no API call)
        projectHasGems(projectName) {
            const originalProject = this.allGroupedProjects.find(p => p.name === projectName);
            if (!originalProject)
                return false;
            // Check if any session in any category has gems
            const hasGems = originalProject.categories.some(category => category.sessions.some((session) => session.hasGems));
            return hasGems;
        },
        limitSessionsInProjectsWithExpansion(projects, limit) {
            return projects.map(project => ({
                ...project,
                categories: project.categories.map(category => {
                    const categoryKey = `${project.name}|${category.name}`;
                    const isExpanded = this.expandedCategories[categoryKey] || false;
                    const sessionsToShow = isExpanded ? category.sessions : category.sessions.slice(0, limit);
                    console.log('[SessionList] Building category with key:', categoryKey);
                    return {
                        ...category,
                        sessions: sessionsToShow,
                        hasMore: !isExpanded && category.sessions.length > limit,
                        hiddenCount: !isExpanded ? Math.max(0, category.sessions.length - limit) : 0,
                        isExpanded: isExpanded,
                        categoryKey: categoryKey,
                        totalSessions: category.sessions.length // Store original count
                    };
                }),
                // Add project ID for gems API
                projectId: this.getProjectIdFromSessions(project)
            }));
        },
        getProjectIdFromSessions(project) {
            // Try to get projectId from first session (NEW: direct field)
            const firstSession = project.categories[0]?.sessions[0];
            if (firstSession?.projectId) {
                console.log(`[SessionList] Using projectId '${firstSession.projectId}' for project '${project.name}'`);
                return firstSession.projectId;
            }
            // Fallback: Extract from folderPath (backward compatibility)
            if (firstSession?.folderPath) {
                // Normalize Windows paths to Unix-style before matching
                const normalizedPath = firstSession.folderPath.replace(/\\/g, '/');
                const pathMatch = normalizedPath.match(/\/projects\/(p_[^/]+)\//i);
                if (pathMatch && pathMatch[1]) {
                    console.log(`[SessionList] Extracted project ID '${pathMatch[1]}' from folderPath for project '${project.name}'`);
                    return pathMatch[1];
                }
            }
            // Last resort: use project name (may cause API errors)
            console.warn(`[SessionList] Could not extract project ID for project '${project.name}', using name as fallback`);
            return project.name;
        },
        getCategoryKey(projectName, categoryName) {
            return `${projectName}|${categoryName}`;
        },
        expandCategory(projectName, categoryName) {
            const categoryKey = this.getCategoryKey(projectName, categoryName);
            console.log('[SessionList] Expanding category:', categoryKey);
            // Mark category as expanded (all sessions already loaded)
            this.$set(this.expandedCategories, categoryKey, true);
            // Smooth scroll to the expanded content after a short delay
            this.$nextTick(() => {
                setTimeout(() => {
                    const categoryElement = document.querySelector(`[data-category-key="${categoryKey}"]`);
                    if (categoryElement) {
                        categoryElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }
                }, 100);
            });
        },
        collapseCategory(projectName, categoryName) {
            const categoryKey = this.getCategoryKey(projectName, categoryName);
            console.log('[SessionList] Collapsing category:', categoryKey);
            this.$delete(this.expandedCategories, categoryKey);
        },
        resetAllCategories() {
            console.log('[SessionList] Resetting all expanded categories');
            this.expandedCategories = {};
        },
        getScrollableHeight() {
            const sessionsPerPage = this.sessionCountStore.sessionsPerPage;
            if (sessionsPerPage === 'all') {
                return 'unset'; // Remove max-height constraint for "all"
            }
            // Increase estimate - session cards are likely taller than 80px
            const sessionHeight = 120; // More realistic height including margins
            const maxHeight = sessionsPerPage * sessionHeight;
            console.log(`[SessionList] Calculated scrollable height: ${maxHeight}px for ${sessionsPerPage} sessions`);
            return `${maxHeight}px`;
        },
        onSessionsPerPageChanged(newCount) {
            console.log('[SessionList] Sessions per page changed to:', newCount);
            // Reset all expanded categories when user changes session count
            this.resetAllCategories();
        },
        async showMoreSessions() {
            console.log('[SessionList] Show more sessions clicked');
            this.isLoadingMore = true;
            // Simulate loading delay for smooth UX
            await new Promise(resolve => setTimeout(resolve, 500));
            // Get current limit and double it (or show reasonable increment)
            const currentLimit = this.sessionCountStore.sessionsPerPage;
            const newLimit = Math.min(currentLimit * 2, 20);
            // If we've reached max reasonable limit, show all
            if (newLimit >= 20 && this.remainingSessionsCount > 20) {
                await this.sessionCountStore.updateSessionsPerPage('all');
            }
            else {
                await this.sessionCountStore.updateSessionsPerPage(newLimit);
            }
            this.isLoadingMore = false;
        },
        /**
         * Handle organization change - reload sessions for new org
         */
        async onOrgChanged(orgId) {
            console.log('[SessionList] Organization changed:', orgId);
            await this.loadSessions();
        },
        /**
         * Handle org creation - reload sessions
         */
        async onOrgCreated() {
            console.log('[SessionList] New organization created');
            await this.loadSessions();
        },
        /**
         * Handle project moved to different organization - reload sessions
         */
        async onProjectMoved(event) {
            console.log('[SessionList] Project moved to different organization:', event);
            await this.loadSessions();
        }
    },
    watch: {
        /**
         * Watch for route query changes (search navigation via query params)
         */
        '$route.query': {
            immediate: true,
            handler() {
                this.$nextTick(() => {
                    this.handleSearchNavigation();
                });
            },
        },
    },
    async created() {
        document.title = "Super Facilitator";
        // Initialize session count store first
        await this.sessionCountStore.initialize();
        // Load organizations BEFORE loading sessions (critical for filtering)
        await organizationStore.loadOrganizations();
        // Load user preferences (expand/collapse state)
        await this.loadUserPreferences();
        // Then load sessions (will use currentOrganizationId from store)
        await this.loadSessions();
    }
});
